import { useCallback, useEffect, useRef, useState } from 'react'
import { SCRIBE_DEFAULT_MAX_DURATION_MS, SCRIBE_MAX_DURATION_MS } from '@tenfour/contracts'

// Scribe capture hook (plans/scribe.md): getUserMedia + MediaRecorder for the
// upload blob, plus an AnalyserNode tap for the live waveform. Deliberately
// NOT built on useVoiceCall — that pipeline is lossy 8kHz μ-law telephony;
// Scribe records the full-rate stream so server transcription hears everything.

export type ScribeRecorderState = 'idle' | 'requesting' | 'recording'
export type ScribeRecorderErrorKind = 'permission-denied' | 'not-supported' | 'no-audio' | 'failed'
export type ScribeRecording = { blob: Blob; mimeType: string; durationMs: number }

// First supported container wins: Chrome/Firefox opus, then iOS Safari AAC,
// then whatever the browser default is (empty string = constructor default).
const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']

export function scribeRecordingSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.MediaRecorder !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia
  )
}

function negotiateMimeType(): string {
  for (const candidate of MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(candidate)) return candidate
  }
  return ''
}

export function useScribeRecorder(opts: {
  maxDurationMs?: number
  /** Fires on every stop — manual or the auto-stop at the duration cap. */
  onRecording: (recording: ScribeRecording) => void
  onError: (kind: ScribeRecorderErrorKind, message: string) => void
}) {
  const [state, setState] = useState<ScribeRecorderState>('idle')
  const [elapsedMs, setElapsedMs] = useState(0)

  const sessionRef = useRef(0)
  const streamRef = useRef<MediaStream | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<BlobPart[]>([])
  const audioCtxRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const analyserDataRef = useRef<Uint8Array<ArrayBuffer> | null>(null)
  const startedAtRef = useRef(0)
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const autoStopRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const discardRef = useRef(false)

  const onRecordingRef = useRef(opts.onRecording)
  onRecordingRef.current = opts.onRecording
  const onErrorRef = useRef(opts.onError)
  onErrorRef.current = opts.onError

  const maxDurationMs = Math.min(
    opts.maxDurationMs ?? SCRIBE_DEFAULT_MAX_DURATION_MS,
    SCRIBE_MAX_DURATION_MS,
  )

  const cleanup = useCallback(() => {
    if (tickRef.current) clearInterval(tickRef.current)
    tickRef.current = null
    if (autoStopRef.current) clearTimeout(autoStopRef.current)
    autoStopRef.current = null
    recorderRef.current = null
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
    analyserRef.current = null
    analyserDataRef.current = null
    void audioCtxRef.current?.close().catch(() => {})
    audioCtxRef.current = null
  }, [])

  // Unmount = hard cancel: never leave a hot mic behind.
  useEffect(() => {
    return () => {
      sessionRef.current += 1
      discardRef.current = true
      try {
        recorderRef.current?.stop()
      } catch {
        /* already stopped */
      }
      cleanup()
    }
  }, [cleanup])

  const start = useCallback(async () => {
    if (recorderRef.current) return
    if (!scribeRecordingSupported()) {
      onErrorRef.current('not-supported', 'Voice capture is not supported in this browser.')
      return
    }
    const session = ++sessionRef.current
    discardRef.current = false
    setState('requesting')
    setElapsedMs(0)

    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        // The useVoiceCall constraint set — but never its 8kHz sampleRate pin,
        // which is a telephony detail that would starve transcription.
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      })
    } catch (err) {
      if (sessionRef.current === session) {
        setState('idle')
        const name = (err as DOMException)?.name
        if (name === 'NotAllowedError' || name === 'SecurityError') {
          onErrorRef.current('permission-denied', 'Microphone access was blocked — allow it and try again, or type instead.')
        } else if (name === 'NotFoundError' || name === 'NotReadableError') {
          onErrorRef.current('no-audio', 'No microphone was found — you can type instead.')
        } else {
          onErrorRef.current('failed', 'Could not start the microphone — you can type instead.')
        }
      }
      return
    }
    // The session guard: a late getUserMedia resolve after cancel/unmount must
    // stop the tracks instead of leaking a hot mic (the useVoiceCall pattern).
    if (sessionRef.current !== session) {
      stream.getTracks().forEach((t) => t.stop())
      return
    }
    streamRef.current = stream

    try {
      // Created inside the user-gesture call path so it starts running, not
      // 'suspended'. Default sample rate on purpose.
      const audioCtx = new AudioContext()
      audioCtxRef.current = audioCtx
      const source = audioCtx.createMediaStreamSource(stream)
      const analyser = audioCtx.createAnalyser()
      analyser.fftSize = 256
      source.connect(analyser)
      analyserRef.current = analyser
      analyserDataRef.current = new Uint8Array(new ArrayBuffer(analyser.fftSize))

      const mimeType = negotiateMimeType()
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream)
      recorderRef.current = recorder
      chunksRef.current = []
      recorder.ondataavailable = (e: BlobEvent) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }
      recorder.onerror = () => {
        if (sessionRef.current !== session) return
        sessionRef.current += 1
        cleanup()
        setState('idle')
        onErrorRef.current('failed', 'Recording failed — you can type instead.')
      }
      recorder.onstop = () => {
        if (sessionRef.current !== session || discardRef.current) return
        const durationMs = Date.now() - startedAtRef.current
        const type = recorder.mimeType || mimeType || 'audio/webm'
        // Container type only ('audio/webm', no codec suffix) — the server
        // whitelist validates against the bare mime.
        const bareType = type.split(';')[0]
        const blob = new Blob(chunksRef.current, { type: bareType })
        sessionRef.current += 1
        cleanup()
        setState('idle')
        if (blob.size === 0) {
          onErrorRef.current('no-audio', 'We did not catch any audio — try again or type instead.')
          return
        }
        onRecordingRef.current({ blob, mimeType: bareType, durationMs })
      }

      startedAtRef.current = Date.now()
      recorder.start(1000)
      setState('recording')
      tickRef.current = setInterval(() => setElapsedMs(Date.now() - startedAtRef.current), 250)
      autoStopRef.current = setTimeout(() => {
        try {
          recorder.stop()
        } catch {
          /* already stopped */
        }
      }, maxDurationMs)
    } catch {
      sessionRef.current += 1
      cleanup()
      setState('idle')
      onErrorRef.current('failed', 'Could not start recording — you can type instead.')
    }
  }, [cleanup, maxDurationMs])

  const stop = useCallback(() => {
    try {
      recorderRef.current?.stop()
    } catch {
      /* already stopped */
    }
  }, [])

  const cancel = useCallback(() => {
    sessionRef.current += 1
    discardRef.current = true
    try {
      recorderRef.current?.stop()
    } catch {
      /* already stopped */
    }
    cleanup()
    setState('idle')
    setElapsedMs(0)
  }, [cleanup])

  // Stable amplitude reader for the waveform: RMS of the time-domain signal,
  // normalized 0..1. Read via ref — no per-frame React state.
  const getAmplitude = useCallback((): number => {
    const analyser = analyserRef.current
    const data = analyserDataRef.current
    if (!analyser || !data) return 0
    analyser.getByteTimeDomainData(data)
    let sum = 0
    for (let i = 0; i < data.length; i++) {
      const v = (data[i] - 128) / 128
      sum += v * v
    }
    return Math.min(1, Math.sqrt(sum / data.length) * 3)
  }, [])

  return { state, elapsedMs, maxDurationMs, start, stop, cancel, getAmplitude }
}
